import { useNavigate } from "#src/Routers/hooks";
import { ErrorDetailsCard } from "#src/components/Common/ErrorDetailsCard";
import config from "#src/config";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { HomeRoute } from "#src/routes/home";
import {
  Button,
  EmptyState,
  EmptyStateProps,
  Icon,
  Link,
} from "@validereinc/common-components";
import { normalizeError } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React, { useRef, useState } from "react";
import styles from "./UnknownError.module.css";

const cx = classNames.bind(styles);

export const UnknownErrorLayout = ({
  error,
  action: providedAction,
}: { error: unknown } & Pick<EmptyStateProps, "action">) => {
  const navigate = useNavigate();
  const { sentryFeedbackInstance } = useAuthenticatedContext();
  const [needToContactSupport, setNeedToContactSupport] = useState(false);
  const normalizedError = useRef<ReturnType<typeof normalizeError> | null>(
    null
  );

  if (normalizedError.current === null) {
    normalizedError.current = normalizeError(error);
  }

  const errName = normalizedError.current.name;
  const errCode = normalizedError.current.code;
  const errMessage = normalizedError.current.message;
  const errExplanation = normalizedError.current.getExplanation();
  const action =
    typeof providedAction !== "undefined" ? (
      providedAction
    ) : (
      <Button
        variant="primary"
        onClick={() => {
          navigate({
            pathname: HomeRoute.path,
          });
        }}
      >
        Go to Homepage
      </Button>
    );

  return (
    <EmptyState
      icon={<Icon variant="smiley-sad" />}
      variant="error"
      title="We're sorry, something went wrong."
      suggestion={({ className }) => (
        <>
          <p className={className}>
            &nbsp;
            {sentryFeedbackInstance && (
              <>
                <Link
                  onClick={() => {
                    if (!sentryFeedbackInstance) return;

                    setNeedToContactSupport(true);
                    sentryFeedbackInstance.createForm().then((f) => {
                      f.appendToDom();
                      f.open();
                    });
                  }}
                  label="Report the bug"
                />
                &nbsp;or&nbsp;
              </>
            )}
            <Link
              anchorTagProps={{
                href: `mailto:${config.SUPPORT_EMAIL}?subject=${
                  errCode
                    ? `Bug Report: ${errName} (name: ${normalizedError.current?.nameWithCode ?? errName}, details: ${encodeURIComponent(errMessage)}) in the Validere Web App`
                    : `Bug Report: ${errName} (details: ${encodeURIComponent(errMessage)}) in the Validere Web App`
                }`,
              }}
              label={
                sentryFeedbackInstance ? "contact support" : "Contact support"
              }
              onClick={() => setNeedToContactSupport(true)}
            />{" "}
            to provide more context so we can fix the issue faster.
          </p>
          <div className={cx("error-details")}>
            <Button
              variant="ghost"
              className={cx("error-details-trigger")}
              onClick={() => setNeedToContactSupport(!needToContactSupport)}
              slotRight={
                <Icon
                  variant={needToContactSupport ? "caret-up" : "caret-down"}
                  className={cx("error-details-icon")}
                />
              }
            >
              {needToContactSupport
                ? "Hide error details"
                : "Show error details"}
            </Button>{" "}
            {needToContactSupport && (
              <div className={cx("error-detail-cards-container")}>
                <ErrorDetailsCard
                  title="Name"
                  content={String(errName ?? "UnknownError")}
                />
                <ErrorDetailsCard
                  title="Code"
                  content={String(errCode ?? "n/a")}
                />
                <ErrorDetailsCard
                  title="Explanation"
                  content={errExplanation}
                />
                <ErrorDetailsCard
                  title="Technical Message"
                  content={errMessage}
                />
                <ErrorDetailsCard
                  title="Payload"
                  content={JSON.stringify(
                    normalizedError.current?.toJSON({
                      exclude: [
                        "stack",
                        "name",
                        "code",
                        "defaultExplanation",
                        "message",
                      ],
                    }) ?? {},
                    null,
                    2
                  )}
                />
              </div>
            )}
          </div>
        </>
      )}
      className={cx("container")}
      action={action}
    />
  );
};
